import React, { useMemo } from "react";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";

interface OrderSummaryProps {
  order: Order;
}

interface InfoItemProps {
  label: string;
  value: React.ReactNode;
  spanFull?: boolean;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value, spanFull }) => (
  <div className={spanFull ? "md:col-span-2" : ""}>
    <p className="text-sm text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-lg font-semibold text-gray-900">{value}</p>
  </div>
);

const RowItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between items-center">
    <span className="text-gray-600">{label}</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

const AmountCard = ({ label, value, valueClass }: { label: string; value: React.ReactNode; valueClass: string }) => (
  <div className="flex justify-between items-center bg-gray-50 px-2 py-2 rounded-xl">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className={`text-lg font-semibold ${valueClass}`}>{value}</span>
  </div>
);

const OrderSummary: React.FC<OrderSummaryProps> = ({ order }) => {
  const formatAmount = useFormatAmount();

  const receivedAmount = useMemo(() => {
    if (!order?.orders?.length) return 0;

    const receivedAmount = order.orders.reduce((sum, order) => {
      return sum + Number(order.receivedAmount || 0) + Number(order.ledgerReceivedAmount || 0);
    }, 0);

    const finalAmount = order.orders.reduce((sum, order) => {
      return sum + Number(order.finalAmount || 0);
    }, 0);

    return Math.min(receivedAmount, finalAmount);
  }, [order]);
  const remainingAmount = order?.orders?.length ? Math.max(Number(order.orders.at(-1)?.remainingAmount ?? 0), 0) : 0;
  const { date: orderDate, time: orderTime } = splitDateTime(order?.orderDetails[0]?.orderedOn?.$date ?? "");
  const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName].filter(Boolean).join(" ");
  const subtotal = (Number(order?.orders[0]?.finalAmount || 0) + Number(order?.orders[0]?.discount || 0)).toFixed(2);
  const discount = order?.orders[0]?.discount || 0;
  const totalAmount = order?.orders[0]?.finalAmount || "0";
  const paymentMethod = order?.paymentHistory[0]?.paymentMethod || "N/A";
  const staff = order?.orders?.[0]?.staffDetails;
  const staffName = [staff?.firstName, staff?.middleName, staff?.lastName].filter(Boolean).join(" ") || "N/A";

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 space-y-2">
        {/* ================= Customer Details ================= */}
        <div className="space-y-2">
          {/* Customer Name - Full width */}
          <InfoItem label="Customer Name" value={customerName || "N/A"} spanFull={true} />
          <InfoItem label="Staff" value={staffName} spanFull={true} />

          {/* Phone Number and Staff in two columns */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <InfoItem label="Phone Number" value={order.customerDetails.phones[0]?.phone || "N/A"} />
          </div> */}
        </div>

        {/* ================= Financial Summary ================= */}
        <div className="space-y-2 pt-2 border-t border-gray-200">
          <RowItem label="Subtotal" value={`${subtotal} QAR`} />
          <RowItem label="Discount" value={`${formatAmount(discount)} QAR`} />
          <RowItem label="Total Amount" value={`${formatAmount(totalAmount)} QAR`} />
          <RowItem label="Payment Method" value={paymentMethod} />
          <RowItem label="Order Date & Time" value={`${orderDate} · ${orderTime}`} />
        </div>

        {/* ================= Received / Remaining ================= */}
        <div className="space-y-3 pt-4 border-t border-gray-200">
          <AmountCard label="Received Amount" value={`${formatAmount(receivedAmount)} QAR`} valueClass="text-emerald-600" />
          <AmountCard label="Remaining Amount" value={`${formatAmount(remainingAmount)} QAR`} valueClass={remainingAmount > 0 ? "text-amber-600" : "text-emerald-600"} />
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
